import React, { useState } from 'react';
import { Calendar, Clock, CheckCircle, AlertCircle, User, FileText, Star, ChevronRight, X, Download, Eye } from 'lucide-react';
interface HomeworkFile {
  id: number;
  name: string;
  size: string;
  type: string;
  uploadedBy?: 'student' | 'teacher';
}
interface Homework {
  id: number;
  title: string;
  subject: string;
  teacherName: string;
  dueDate: string;
  daysLeft?: number;
  status: 'active' | 'submitted' | 'graded';
  grade?: {
    score: number;
    total: number;
  };
  feedback?: string;
  description: string;
  attachments?: HomeworkFile[];
  submittedDate?: string;
  gradedDate?: string;
  isEarly?: boolean;
  notes?: string;
  teacherFeedback?: string;
}
function VeliOdevlerim() {
  const [activeTab, setActiveTab] = useState<'active' | 'submitted' | 'graded'>('active');
  const [selectedHomework, setSelectedHomework] = useState<Homework | null>(null);
  // Format date function
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };
  // Calculate days left
  const calculateDaysLeft = (dueDate: string) => {
    const today = new Date('2025-09-05'); // Current date
    const due = new Date(dueDate);
    const diffTime = due.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };
  // Active homework data
  const activeHomework = [{
    id: 1,
    title: 'Matematik Ders Kitabı Sayfa 45-50',
    subject: 'MATEMATİK',
    teacherName: 'Ayşe Öğretmen',
    dueDate: '2025-09-08',
    daysLeft: 3,
    status: 'active' as const,
    description: 'Rasyonel sayılar konusundaki alıştırmaları çözün.',
    notes: 'Grafik çizmeyi unutmayın.',
    attachments: [{
      id: 1,
      name: 'Matematik Alıştırma Sayfaları.pdf',
      size: '2.4 MB',
      type: 'pdf',
      uploadedBy: 'teacher' as const
    }]
  }, {
    id: 2,
    title: 'Türkçe Kompozisyon',
    subject: 'TÜRKÇE',
    teacherName: 'Mehmet Öğretmen',
    dueDate: '2025-09-12',
    daysLeft: 7,
    status: 'active' as const,
    description: 'En sevdiğiniz kitap hakkında 200 kelimelik kompozisyon yazın.',
    notes: 'A4 kağıdına el yazısı ile yazın.',
    attachments: [{
      id: 2,
      name: 'Kompozisyon Yönergeleri.docx',
      size: '1.5 MB',
      type: 'docx',
      uploadedBy: 'teacher' as const
    }]
  }, {
    id: 3,
    title: 'Fen Bilimleri Deney Raporu',
    subject: 'FEN BİLİMLERİ',
    teacherName: 'Fatma Öğretmen',
    dueDate: '2025-09-10',
    daysLeft: 5,
    status: 'active' as const,
    description: 'Bitkilerde fotosentez deneyi yapın ve raporlayın.',
    notes: 'Fotoğrafları da ekleyin.',
    attachments: [{
      id: 3,
      name: 'Deney Kılavuzu.pdf',
      size: '1.8 MB',
      type: 'pdf',
      uploadedBy: 'teacher' as const
    }]
  }];
  // Submitted homework data
  const submittedHomework = [{
    id: 4,
    title: 'İngilizce Kelime Listesi',
    subject: 'İNGİLİZCE',
    teacherName: 'John Öğretmen',
    dueDate: '2025-09-03',
    submittedDate: '2025-09-02',
    status: 'submitted' as const,
    description: "Unit 3'teki kelimelerin Türkçe karşılıkları.",
    isEarly: true,
    attachments: [{
      id: 4,
      name: 'Unit 3 Vocabulary.pdf',
      size: '1.8 MB',
      type: 'pdf',
      uploadedBy: 'student' as const
    }]
  }, {
    id: 5,
    title: 'Tarih Araştırma Ödevi',
    subject: 'T.C. İNKILAP TARİHİ VE ATATÜRKÇÜLÜK',
    teacherName: 'Ahmet Öğretmen',
    dueDate: '2025-09-01',
    submittedDate: '2025-09-01',
    status: 'submitted' as const,
    description: "Kurtuluş Savaşı'nda kadınların rolü hakkında araştırma.",
    isEarly: false,
    attachments: [{
      id: 5,
      name: 'Kurtuluş Savaşı Araştırma.pdf',
      size: '3.2 MB',
      type: 'pdf',
      uploadedBy: 'student' as const
    }]
  }];
  // Graded homework data
  const gradedHomework = [{
    id: 6,
    title: 'Matematik Problem Çözme',
    subject: 'MATEMATİK',
    teacherName: 'Ayşe Öğretmen',
    dueDate: '2025-08-28',
    submittedDate: '2025-08-27',
    gradedDate: '2025-08-30',
    status: 'graded' as const,
    description: 'Denklem sistemleri problemleri.',
    teacherFeedback: 'Çok başarılı! Sadece 3. soruda küçük bir hata var.',
    grade: {
      score: 85,
      total: 100
    },
    attachments: [{
      id: 6,
      name: 'Matematik_Problemler_Çözüm.pdf',
      size: '1.8 MB',
      type: 'pdf',
      uploadedBy: 'student' as const
    }]
  }, {
    id: 7,
    title: 'Edebiyat Analizi',
    subject: 'TÜRKÇE',
    teacherName: 'Mehmet Öğretmen',
    dueDate: '2025-08-25',
    submittedDate: '2025-08-24',
    gradedDate: '2025-08-28',
    status: 'graded' as const,
    description: "Necip Fazıl'ın 'Çile' şiiri analizi.",
    teacherFeedback: 'Mükemmel analiz! Kelime seçimlerin çok güzel.',
    grade: {
      score: 92,
      total: 100
    },
    attachments: [{
      id: 7,
      name: 'Şiir_Analizi.docx',
      size: '1.2 MB',
      type: 'docx',
      uploadedBy: 'student' as const
    }]
  }];
  // Get homework based on active tab
  const getHomeworkByTab = () => {
    if (activeTab === 'active') return activeHomework;
    if (activeTab === 'submitted') return submittedHomework;
    return gradedHomework;
  };
  // Filtrelenmiş ödevler
  const filteredHomework = getHomeworkByTab();
  // Renk sistemleri - lacivert tema
  const getSubjectColor = (subject: string) => {
    const colors: Record<string, string> = {
      MATEMATİK: 'from-blue-600 to-blue-700',
      TÜRKÇE: 'from-indigo-600 to-indigo-700',
      'FEN BİLİMLERİ': 'from-cyan-600 to-cyan-700',
      İNGİLİZCE: 'from-sky-600 to-sky-700',
      'T.C. İNKILAP TARİHİ VE ATATÜRKÇÜLÜK': 'from-slate-600 to-slate-700',
      'DİN KÜLTÜRÜ VE AHLÂK BİLGİSİ': 'from-gray-600 to-gray-700'
    };
    return colors[subject] || 'from-blue-600 to-indigo-600';
  };
  // Durum ikonunu getir
  const getStatusIcon = (homework: Homework) => {
    if (homework.daysLeft !== undefined && homework.daysLeft < 0 && homework.status === 'active') {
      return <AlertCircle className="w-5 h-5 text-red-400" data-id="element-855" />;
    }
    if (homework.daysLeft === 0 && homework.status === 'active') {
      return <Clock className="w-5 h-5 text-yellow-400" data-id="element-856" />;
    }
    if (homework.status === 'submitted') {
      return <CheckCircle className="w-5 h-5 text-green-400" data-id="element-857" />;
    }
    if (homework.status === 'graded') {
      return <Star className="w-5 h-5 text-blue-400" data-id="element-858" />;
    }
    return <Calendar className="w-5 h-5 text-blue-400" data-id="element-859" />;
  };
  return <div className="space-y-6" data-id="element-860">
      <h1 className="text-2xl font-bold" data-id="element-861">Ödev Takibi</h1>
      {/* Tab Sistemi - Lacivert Tema */}
      <div className="flex justify-center mb-6" data-id="element-862">
        <div className="inline-flex bg-black/30 rounded-lg p-1" data-id="element-863">
          <button onClick={() => setActiveTab('active')} className={`px-6 py-3 rounded-lg transition-all duration-300 ${activeTab === 'active' ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg shadow-blue-500/25' : 'text-gray-300 hover:bg-white/10 hover:text-white'}`} data-id="element-864">
            Aktif Ödevler
          </button>
          <button onClick={() => setActiveTab('submitted')} className={`px-6 py-3 rounded-lg transition-all duration-300 ${activeTab === 'submitted' ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg shadow-blue-500/25' : 'text-gray-300 hover:bg-white/10 hover:text-white'}`} data-id="element-865">
            Teslim Edilenler
          </button>
          <button onClick={() => setActiveTab('graded')} className={`px-6 py-3 rounded-lg transition-all duration-300 ${activeTab === 'graded' ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg shadow-blue-500/25' : 'text-gray-300 hover:bg-white/10 hover:text-white'}`} data-id="element-866">
            Notlandırılanlar
          </button>
        </div>
      </div>
      {/* ÖDEVLERİ LİSTELE - TEMİZ VE HAVADAR TASARIM */}
      <div className="space-y-4" data-id="element-867">
        {filteredHomework.length > 0 ? filteredHomework.map(homework => <div key={homework.id} className={`relative p-[1px] rounded-xl overflow-hidden bg-gradient-to-r ${getSubjectColor(homework.subject)} group hover:shadow-[0_0_20px_rgba(255,255,255,0.1)] transition-all duration-300`} data-id="element-868">
              <div className="bg-black/90 p-5 rounded-xl" data-id="element-869">
                {/* ÜST KISIM - BAŞLIK VE DURUM */}
                <div className="flex items-center justify-between mb-3" data-id="element-870">
                  <div className="flex items-center gap-3" data-id="element-871">
                    {getStatusIcon(homework)}
                    <div data-id="element-872">
                      <h3 className="text-lg font-bold text-white" data-id="element-873">
                        {homework.title}
                      </h3>
                      <div className="flex items-center gap-2 text-sm text-gray-400" data-id="element-874">
                        <span data-id="element-875">{homework.subject}</span>
                        <span data-id="element-876">•</span>
                        <span data-id="element-877">{homework.teacherName}</span>
                      </div>
                    </div>
                  </div>
                  {/* SAĞDA TARİH VE UYARI */}
                  <div className="text-right" data-id="element-878">
                    <div className="text-sm text-gray-300" data-id="element-879">
                      {formatDate(homework.dueDate)}
                    </div>
                    {homework.daysLeft !== undefined && homework.daysLeft < 0 && homework.status === 'active' && <div className="text-red-400 text-sm font-medium" data-id="element-880">
                          {Math.abs(homework.daysLeft)} gün gecikmiş
                        </div>}
                    {homework.daysLeft === 0 && homework.status === 'active' && <div className="text-yellow-400 text-sm font-medium" data-id="element-881">
                          Bugün son gün!
                        </div>}
                    {homework.daysLeft !== undefined && homework.daysLeft > 0 && homework.status === 'active' && <div className="text-green-400 text-sm" data-id="element-882">
                          {homework.daysLeft} gün kaldı
                        </div>}
                    {homework.status === 'submitted' && homework.isEarly && <div className="text-green-400 text-sm font-medium" data-id="element-883">
                        Erken teslim edildi
                      </div>}
                    {homework.status === 'submitted' && !homework.isEarly && <div className="text-blue-400 text-sm font-medium" data-id="element-884">
                        Zamanında teslim
                      </div>}
                  </div>
                </div>
                {/* NOT BİLGİSİ - SADECE NOTLANDIRILANLARDA */}
                {homework.grade && <div className="mb-3 flex items-center gap-2" data-id="element-885">
                    <Star className="w-4 h-4 text-yellow-400" data-id="element-886" />
                    <span className="text-yellow-400 font-medium" data-id="element-887">
                      Not: {homework.grade.score}/{homework.grade.total} (%
                      {Math.round(homework.grade.score / homework.grade.total * 100)}
                      )
                    </span>
                  </div>}
                {/* KISA AÇIKLAMA - TEK SATIR */}
                <div className="mb-4" data-id="element-888">
                  <p className="text-gray-300 text-sm line-clamp-1" data-id="element-889">
                    {homework.description}
                  </p>
                </div>
                {/* ALT KISIM - DOSYA SAYISI VE DETAY BUTONU */}
                <div className="flex items-center justify-between" data-id="element-890">
                  <div className="flex items-center gap-4 text-sm text-gray-400" data-id="element-891">
                    {homework.attachments && homework.attachments.length > 0 && <div className="flex items-center gap-1" data-id="element-892">
                          <FileText className="w-4 h-4" data-id="element-893" />
                          <span data-id="element-894">{homework.attachments.length} dosya</span>
                        </div>}
                    {homework.status === 'submitted' && <div className="flex items-center gap-1 text-green-400" data-id="element-895">
                        <CheckCircle className="w-4 h-4" data-id="element-896" />
                        <span data-id="element-897">Teslim edildi</span>
                      </div>}
                  </div>
                  {/* DETAY BUTONU */}
                  <button onClick={() => setSelectedHomework(homework)} className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm" data-id="element-898">
                    <Eye className="w-4 h-4" data-id="element-899" />
                    <span data-id="element-900">Detay</span>
                  </button>
                </div>
                {/* PARLAMA EFEKTİ */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[rgba(255,255,255,0.1)] to-transparent group-hover:translate-x-full -skew-x-12 transition-transform duration-1000 ease-out" data-id="element-901" />
              </div>
            </div>) :
      // BOŞ DURUM MESAJI - DEĞİŞMEZ
      <div className="text-center py-12" data-id="element-902">
            <FileText className="w-16 h-16 text-gray-600 mx-auto mb-4" data-id="element-903" />
            <h3 className="text-xl font-medium text-gray-400 mb-2" data-id="element-904">
              {activeTab === 'active' ? 'Aktif ödev bulunmuyor' : activeTab === 'submitted' ? 'Teslim edilmiş ödev bulunmuyor' : 'Notlandırılmış ödev bulunmuyor'}
            </h3>
          </div>}
      </div>
      {/* Ödev Detay Modal - SADECE GÖRÜNTÜLEME */}
      {selectedHomework && <div className="fixed inset-0 bg-black/90 flex items-center justify-center p-4 z-50" data-id="element-905">
          <div className="relative p-[1px] rounded-xl overflow-hidden bg-gradient-to-r from-blue-600 to-indigo-600 w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-id="element-906">
            <div className="relative bg-black rounded-xl" data-id="element-907">
              <div className="p-6 border-b border-blue-900/30 flex justify-between items-center" data-id="element-908">
                <h2 className="text-xl font-bold" data-id="element-909">{selectedHomework.title}</h2>
                <button onClick={() => setSelectedHomework(null)} className="text-gray-400 hover:text-white" data-id="element-910">
                  <X className="w-6 h-6" data-id="element-911" />
                </button>
              </div>
              <div className="p-6 space-y-4" data-id="element-912">
                {/* Ödev bilgileri */}
                <div className="grid grid-cols-2 gap-4 text-sm" data-id="element-913">
                  <div data-id="element-914">
                    <span className="text-gray-400" data-id="element-915">Ders:</span>
                    <span className="ml-2 text-white" data-id="element-916">
                      {selectedHomework.subject}
                    </span>
                  </div>
                  <div data-id="element-917">
                    <span className="text-gray-400" data-id="element-918">Öğretmen:</span>
                    <span className="ml-2 text-white" data-id="element-919">
                      {selectedHomework.teacherName}
                    </span>
                  </div>
                  <div data-id="element-920">
                    <span className="text-gray-400" data-id="element-921">Son Teslim:</span>
                    <span className="ml-2 text-white" data-id="element-922">
                      {formatDate(selectedHomework.dueDate)}
                    </span>
                  </div>
                  <div data-id="element-923">
                    <span className="text-gray-400" data-id="element-924">Durum:</span>
                    <span className={`ml-2 font-medium ${selectedHomework.status === 'active' ? 'text-yellow-400' : selectedHomework.status === 'submitted' ? 'text-green-400' : 'text-blue-400'}`} data-id="element-925">
                      {selectedHomework.status === 'active' ? 'Aktif' : selectedHomework.status === 'submitted' ? 'Teslim Edildi' : 'Notlandırıldı'}
                    </span>
                  </div>
                  {selectedHomework.submittedDate && <div data-id="element-926">
                      <span className="text-gray-400" data-id="element-927">Teslim Tarihi:</span>
                      <span className="ml-2 text-white" data-id="element-928">
                        {formatDate(selectedHomework.submittedDate)}
                      </span>
                    </div>}
                  {selectedHomework.gradedDate && <div data-id="element-929">
                      <span className="text-gray-400" data-id="element-930">Notlandırma Tarihi:</span>
                      <span className="ml-2 text-white" data-id="element-931">
                        {formatDate(selectedHomework.gradedDate)}
                      </span>
                    </div>}
                </div>
                {/* Açıklama */}
                <div data-id="element-932">
                  <h4 className="text-blue-400 font-medium mb-2" data-id="element-933">Açıklama:</h4>
                  <p className="text-gray-300 text-sm leading-relaxed" data-id="element-934">
                    {selectedHomework.description}
                  </p>
                </div>
                {/* Not ve geri bildirim */}
                {selectedHomework.grade && <div data-id="element-935">
                    <h4 className="text-blue-400 font-medium mb-2" data-id="element-936">Not:</h4>
                    <div className="text-2xl font-bold text-green-400" data-id="element-937">
                      {selectedHomework.grade.score}/
                      {selectedHomework.grade.total}
                    </div>
                  </div>}
                {selectedHomework.teacherFeedback && <div data-id="element-938">
                    <h4 className="text-blue-400 font-medium mb-2" data-id="element-939">
                      Öğretmen Geri Bildirimi:
                    </h4>
                    <p className="text-gray-300 text-sm leading-relaxed" data-id="element-940">
                      {selectedHomework.teacherFeedback}
                    </p>
                  </div>}
                {/* Dosyalar */}
                {selectedHomework.attachments && selectedHomework.attachments.length > 0 && <div data-id="element-941">
                      <h4 className="text-blue-400 font-medium mb-2" data-id="element-942">
                        Ekli Dosyalar:
                      </h4>
                      <div className="space-y-2" data-id="element-943">
                        {selectedHomework.attachments.map(file => <div key={file.id} className="flex items-center gap-3 p-3 bg-blue-900/10 border border-blue-900/30 rounded-lg" data-id="element-944">
                            <FileText className="w-5 h-5 text-blue-400" data-id="element-945" />
                            <div className="flex-1" data-id="element-946">
                              <p className="text-white text-sm font-medium" data-id="element-947">
                                {file.name}
                              </p>
                              <p className="text-gray-400 text-xs" data-id="element-948">
                                {file.size} •{' '}
                                {file.uploadedBy === 'teacher' ? 'Öğretmen' : 'Öğrenci'}
                              </p>
                            </div>
                            <button className="p-2 hover:bg-blue-500/20 rounded-lg text-blue-400 hover:text-white transition-colors" data-id="element-949">
                              <Download className="w-4 h-4" data-id="element-950" />
                            </button>
                          </div>)}
                      </div>
                    </div>}
              </div>
            </div>
          </div>
        </div>}
      {/* CSS EKLEMELERİ */}
      <style jsx global data-id="element-951">{`
        .line-clamp-1 {
          display: -webkit-box;
          -webkit-line-clamp: 1;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>;
}
export { VeliOdevlerim };